import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { HeroHeaderLogo } from "@/components/dataTable/HeroHeaderLogo";
import { useNoteReferenceAction } from "@/projectOverview/notes/useNoteReferenceAction";
import { useCurrentUser, getUserLocalLogin, userIsAbrechnung, userIsBuero, userIsMonteure } from "@/lib/auth";
import { useBirthday } from "@/features/birthday/BirthdayProvider";
import { useDecoPreference } from "@/features/deco/DecoPreferenceProvider";
import {
  decoAllowsHomeBirthdayEffects,
  decoAllowsHomeBirthdayWidget,
} from "@/lib/decoPreference";
import { useRealtime } from "@/lib/useRealtime";
import { MENTIONS_BADGE_EVENTS } from "@/lib/navBadges";
import { useNavBadges } from "@/hooks/useNavBadges";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { userCanApproveZeiterfassung } from "@/lib/zeiterfassungPermissions";
import {
  fetchLatestOrders,
  fetchManagementTimesheetMetrics,
  fetchProjectCompletions,
  fetchTimesheetsWithOpenBillingQuestions,
  fetchReadMentions,
  fetchUnreadMentions,
  filterOpenProjectCompletions,
  formatCurrencyMetric,
  formatHoursMetric,
  getOpenBillingQuestionCount,
  HERO_PROJECT_BASE,
  markMentionRead,
  MENTIONS_PREVIEW_LIMIT,
  type HomeMention,
  type HomeOrderRow,
  type ProjectCompletionRow,
  getProjectCompletionStatusMeta,
} from "@/lib/home";
import type { TimesheetSummary } from "@/lib/timesheets";
import { listAngebotsanfragen, type AgaListItem } from "@/lib/angebotsanfragen";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import {
  getTimesheetDisplayNumber,
  getTimesheetStatus,
  timesheetStatusLabel,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import { AnsweredBillingTimesheetsDialog } from "@/pages/home/AnsweredBillingTimesheetsDialog";
import { BirthdayWidget } from "@/pages/home/BirthdayWidget";
import { HomeApprovalsCards } from "@/pages/home/HomeApprovalsCards";
import {
  BillingHomeCard,
  FieldMaterialCard,
  HomeKpiStrip,
  LieferscheineAtContactCard,
  OfficeOpenPoints,
  type HomeKpiItem,
  type OfficeHomeCounts,
} from "@/pages/home/HomeRoleSections";
import {
  filterNewBillingLieferscheine,
  HOME_PERSONA_SUBTITLE,
  HOME_PERSONA_TITLE,
  resolveHomePersona,
  splitBillingHomeTimesheets,
  type BillingHomeTimesheets,
} from "@/pages/home/homeRole";
import { HomeMissingNumbersTile } from "@/pages/home/HomeMissingNumbersTile";
import { HomeTileBoard, type HomeBoardTile } from "@/pages/home/HomeTileBoard";
import {
  DEFAULT_HOME_LAYOUT,
  getHomeLayout,
  hideHomeTile,
  resolveHomeTileSpan,
  saveHomeLayout,
  setHomeColumns,
  setHomeTileSpan,
  showHomeTile,
  visibleHomeTileIds,
  weaveHomeOrder,
  type HomeColumnCount,
  type HomeLayoutSettings,
  type HomeTileId,
  type HomeTileSpan,
} from "@/pages/home/homeLayout";
import {
  defaultHomeTileIds,
  homeTileAudience,
  permittedHomeTileIds,
} from "@/pages/home/homeTileAccess";
import { HomeBestellwesenProvider, HomeBestellwesenTile } from "@/pages/home/HomeBestellwesenTiles";
import {
  HomePersonalAbsencesTile,
  HomePersonalDataProvider,
  HomePersonalOverviewTile,
} from "@/pages/home/HomePersonalTiles";
import { MyDayPanel } from "@/pages/home/MyDayPanel";
import { SubcontractorOpenCard, TeamAbsencesCard } from "@/pages/personal/hub/PersonalHubWidgets";
import { FeedDetailDialog } from "@/pages/home/FeedDetailDialog";
import { MentionBody } from "@/pages/home/MentionBody";
import { TodoPanel } from "@/pages/home/TodoPanel";
import { TodoQuickDialog } from "@/features/todos/TodoQuickDialog";
import { buildMentionTodoDraft } from "@/lib/todos";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import {
  dokumentTypLabel,
  listDokumenteWithOpenBillingQuestions,
  type DokumentMitOffenenRueckfragen,
} from "@/lib/dokumentBilling";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import { listLieferscheineSummary, type LieferscheinSummary } from "@/lib/lieferscheine";
import { filterLieferscheineAtContact } from "@/pages/lieferscheine/lieferscheinListLogic";
import {
  fetchDeliveredUnseenMaterial,
  fetchMaterialBadgeCounts,
  markMaterialAnforderungSeen,
  type ReadyMaterialItem,
} from "@/lib/material";
import { notifyMaterialBadgeRefresh } from "@/lib/navBadges";
import { fetchAngebotsanfragenOpenCount } from "@/lib/angebotsanfragen";
import {
  fetchFeinplanungRequestsOpenCount,
  fetchMonteurProposalsOpenCount,
} from "@/lib/montageplanung";
import { fetchAnfragenAnswerCount } from "@/lib/anfragen";
import { fetchMahnwesenFaelligCount } from "@/lib/mahnwesen";
import { listTimesheetsSummary } from "@/lib/timesheets";

function formatDateLabel(value?: string | null): string {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("de-DE");
}

export function HomeIndexPage() {
  const { user } = useCurrentUser();
  const { data: birthdays, mineIsBirthday, reload: reloadBirthdays } = useBirthday();
  const { preference: decoPreference } = useDecoPreference();
  const showHomeBirthdayWidget =
    !!birthdays && decoAllowsHomeBirthdayWidget(decoPreference);
  const showHomeBirthdayEffects =
    mineIsBirthday && decoAllowsHomeBirthdayEffects(decoPreference);
  const { openDetail, openLieferscheinDetail, openLieferscheinForProject, refreshKey: timesheetRefreshKey } =
    useTimesheetFlow();
  const { open: openProjectOverview } = useProjectOverview();
  const { open: openDocumentEditor } = useDocumentEditor();
  const { handleRefClick, fallbackModal } = useNoteReferenceAction(0);
  const persona = resolveHomePersona(user);
  const isManagement = persona === "management";
  const isMonteur = userIsMonteure(user);
  const isAbrechnung = userIsAbrechnung(user);
  const badgeCounts = useNavBadges(user);
  const isBuero = userIsBuero(user);
  const canLoadOrders = isBuero || persona === "general";
  const showTodoPanel = Boolean(user);
  const canApproveZeiterfassung = userCanApproveZeiterfassung(user);

  const [orders, setOrders] = useState<HomeOrderRow[] | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [mentions, setMentions] = useState<HomeMention[]>([]);
  const [readMentions, setReadMentions] = useState<HomeMention[]>([]);
  const [mentionsError, setMentionsError] = useState<string | null>(null);
  const [completions, setCompletions] = useState<ProjectCompletionRow[] | null>(null);
  const [completionsError, setCompletionsError] = useState<string | null>(null);
  const [billingQuestions, setBillingQuestions] = useState<TimesheetSummary[] | null>(null);
  const [billingQuestionsError, setBillingQuestionsError] = useState<string | null>(null);
  const [docBillingQuestions, setDocBillingQuestions] = useState<
    DokumentMitOffenenRueckfragen[] | null
  >(null);
  const [angebote, setAngebote] = useState<AgaListItem[] | null>(null);
  const [angeboteError, setAngeboteError] = useState<string | null>(null);
  const [mgmtMetrics, setMgmtMetrics] = useState<{
    billedHours: number;
    invoiceValue: number;
  } | null>(null);
  const [mgmtMetricsError, setMgmtMetricsError] = useState(false);
  const [atContactNotes, setAtContactNotes] = useState<LieferscheinSummary[] | null>(null);
  const [atContactError, setAtContactError] = useState<string | null>(null);
  const [billingInbox, setBillingInbox] = useState<
    (BillingHomeTimesheets & { lieferscheine: LieferscheinSummary[] }) | null
  >(null);
  const [billingInboxError, setBillingInboxError] = useState<string | null>(null);
  const [officeCounts, setOfficeCounts] = useState<OfficeHomeCounts | null>(null);
  const [officeCountsError, setOfficeCountsError] = useState<string | null>(null);
  const [readyMaterial, setReadyMaterial] = useState<ReadyMaterialItem[] | null>(null);
  const [readyMaterialError, setReadyMaterialError] = useState<string | null>(null);
  const [markingMaterialId, setMarkingMaterialId] = useState<number | null>(null);
  const [showAllMentions, setShowAllMentions] = useState(false);
  const [mentionsHistoryOpen, setMentionsHistoryOpen] = useState(false);
  const [answeredBillingOpen, setAnsweredBillingOpen] = useState(false);
  const [mentionTodoDraft, setMentionTodoDraft] = useState<HomeMention | null>(null);

  const reloadOrders = useCallback(async () => {
    if (!canLoadOrders) {
      setOrders(null);
      return;
    }
    try {
      setOrdersError(null);
      setOrders(await fetchLatestOrders(5));
    } catch {
      setOrdersError("Konnte die neuesten Aufträge nicht laden.");
      setOrders([]);
    }
  }, [canLoadOrders]);

  const reloadMentions = useCallback(async () => {
    try {
      setMentionsError(null);
      const [unread, read] = await Promise.all([
        fetchUnreadMentions(),
        fetchReadMentions(),
      ]);
      setMentions(unread);
      setReadMentions(read);
    } catch {
      setMentionsError("Erwähnungen konnten nicht geladen werden.");
      setMentions([]);
      setReadMentions([]);
    }
  }, []);

  const completeMentionRead = useCallback((mention: HomeMention) => {
    setMentions((prev) => prev.filter((x) => x.id !== mention.id));
    setReadMentions((prev) => {
      if (prev.some((x) => x.id === mention.id)) return prev;
      return [{ ...mention, read_at: mention.read_at || new Date().toISOString() }, ...prev];
    });
  }, []);

  const openMentionInProjectOverview = useCallback(
    (mention: HomeMention) => {
      if (!mention.auftrag_id) return;
      openProjectOverview(mention.auftrag_id, { highlightNoteId: mention.note_id });
    },
    [openProjectOverview],
  );

  const replyToMention = useCallback(
    (mention: HomeMention) => {
      if (!mention.auftrag_id) return;
      openProjectOverview(mention.auftrag_id, {
        highlightNoteId: mention.note_id,
        replyDraft: {
          key: `mention-${mention.id}`,
          authorLogin: mention.note_author_login || null,
          authorDisplay: mention.note_author || null,
        },
      });
    },
    [openProjectOverview],
  );

  const reloadCompletions = useCallback(async () => {
    if (!isManagement) {
      setCompletions(null);
      return;
    }
    try {
      setCompletionsError(null);
      const rows = await fetchProjectCompletions();
      setCompletions(filterOpenProjectCompletions(rows));
    } catch {
      setCompletionsError("Fertigstellungsmeldungen konnten nicht geladen werden.");
      setCompletions([]);
    }
  }, [isManagement]);

  const reloadBillingQuestions = useCallback(async () => {
    if (!isManagement) {
      setBillingQuestions(null);
      setDocBillingQuestions(null);
      return;
    }
    setBillingQuestionsError(null);
    // Stundennachweis- und Dokument-Rückfragen getrennt laden, damit ein
    // Fehler in einer Quelle die andere nicht verdeckt.
    const [tsResult, docResult] = await Promise.allSettled([
      fetchTimesheetsWithOpenBillingQuestions(),
      listDokumenteWithOpenBillingQuestions(),
    ]);
    if (tsResult.status === "fulfilled") {
      const rows = tsResult.value;
      rows.sort((a, b) => {
        const openDiff =
          getOpenBillingQuestionCount(b) - getOpenBillingQuestionCount(a);
        if (openDiff !== 0) return openDiff;
        const ta = new Date(a.created_at || a.period_end || 0).getTime();
        const tb = new Date(b.created_at || b.period_end || 0).getTime();
        return tb - ta;
      });
      setBillingQuestions(rows);
    } else {
      setBillingQuestionsError("Offene Rückfragen konnten nicht geladen werden.");
      setBillingQuestions([]);
    }
    setDocBillingQuestions(
      docResult.status === "fulfilled" ? docResult.value : [],
    );
  }, [isManagement]);

  const reloadAngebote = useCallback(async () => {
    if (!isManagement) {
      setAngebote(null);
      return;
    }
    try {
      setAngeboteError(null);
      setAngebote(await listAngebotsanfragen({ status: "offen" }));
    } catch {
      setAngeboteError("Angebotsanfragen konnten nicht geladen werden.");
      setAngebote([]);
    }
  }, [isManagement]);

  const reloadMgmtMetrics = useCallback(async () => {
    if (!isManagement) {
      setMgmtMetrics(null);
      return;
    }
    try {
      setMgmtMetricsError(false);
      setMgmtMetrics(await fetchManagementTimesheetMetrics());
    } catch {
      setMgmtMetricsError(true);
      setMgmtMetrics(null);
    }
  }, [isManagement]);

  const reloadAtContact = useCallback(async () => {
    if (!isManagement) {
      setAtContactNotes(null);
      return;
    }
    try {
      setAtContactError(null);
      setAtContactNotes(filterLieferscheineAtContact(await listLieferscheineSummary()));
    } catch {
      setAtContactError("Lieferscheine beim Ansprechpartner konnten nicht geladen werden.");
      setAtContactNotes([]);
    }
  }, [isManagement]);

  const reloadBillingInbox = useCallback(async () => {
    if (!isAbrechnung) {
      setBillingInbox(null);
      return;
    }
    setBillingInboxError(null);
    const [tsResult, lsResult] = await Promise.allSettled([
      listTimesheetsSummary(),
      listLieferscheineSummary(),
    ]);
    const split =
      tsResult.status === "fulfilled"
        ? splitBillingHomeTimesheets(tsResult.value)
        : { signed: [], drafts: [], questions: [] };
    const lieferscheine =
      lsResult.status === "fulfilled" ? filterNewBillingLieferscheine(lsResult.value) : [];
    if (tsResult.status === "rejected" || lsResult.status === "rejected") {
      setBillingInboxError("Die Abrechnungs-Übersicht konnte nicht vollständig geladen werden.");
    }
    setBillingInbox({ ...split, lieferscheine });
  }, [isAbrechnung]);

  const reloadOfficeCounts = useCallback(async () => {
    if (!isBuero) {
      setOfficeCounts(null);
      setOfficeCountsError(null);
      return;
    }
    const [material, feinplanung, proposals, angeboteCount, mahnungen, anfragen] =
      await Promise.allSettled([
        fetchMaterialBadgeCounts(),
        fetchFeinplanungRequestsOpenCount(),
        fetchMonteurProposalsOpenCount(),
        fetchAngebotsanfragenOpenCount(),
        fetchMahnwesenFaelligCount(),
        fetchAnfragenAnswerCount(),
      ]);
    const failed = [material, feinplanung, proposals, angeboteCount, mahnungen, anfragen].every(
      (result) => result.status === "rejected",
    );
    if (failed) {
      setOfficeCountsError("Offene Punkte konnten nicht geladen werden.");
      setOfficeCounts(null);
      return;
    }
    const materialValue = material.status === "fulfilled" ? material.value : null;
    setOfficeCountsError(null);
    setOfficeCounts({
      materialOpen: materialValue?.open ?? 0,
      materialUrgent: materialValue?.open_urgent_fix ?? 0,
      materialInProgress: materialValue?.in_bearbeitung ?? 0,
      feinplanung: feinplanung.status === "fulfilled" ? feinplanung.value : 0,
      proposals: proposals.status === "fulfilled" ? proposals.value : 0,
      angebote: angeboteCount.status === "fulfilled" ? angeboteCount.value : 0,
      mahnungen: mahnungen.status === "fulfilled" ? mahnungen.value : 0,
      anfragen: anfragen.status === "fulfilled" ? anfragen.value : 0,
    });
  }, [isBuero]);

  const reloadReadyMaterial = useCallback(async () => {
    if (!isMonteur) {
      setReadyMaterial(null);
      setReadyMaterialError(null);
      return;
    }
    try {
      setReadyMaterialError(null);
      setReadyMaterial(await fetchDeliveredUnseenMaterial());
    } catch {
      setReadyMaterialError("Bereitgelegtes Material konnte nicht geladen werden.");
      setReadyMaterial([]);
    }
  }, [isMonteur]);

  const markReadyMaterialSeen = useCallback(async (id: number) => {
    setMarkingMaterialId(id);
    setReadyMaterialError(null);
    try {
      await markMaterialAnforderungSeen(id);
      setReadyMaterial((prev) => (prev || []).filter((row) => row.id !== id));
      notifyMaterialBadgeRefresh();
    } catch {
      setReadyMaterialError("Die Anforderung konnte nicht als gelesen markiert werden.");
    } finally {
      setMarkingMaterialId(null);
    }
  }, []);

  const reloadAll = useCallback(() => {
    void reloadBirthdays();
    void reloadOrders();
    void reloadMentions();
    void reloadCompletions();
    void reloadBillingQuestions();
    void reloadAngebote();
    void reloadMgmtMetrics();
    void reloadAtContact();
    void reloadBillingInbox();
    void reloadOfficeCounts();
    void reloadReadyMaterial();
  }, [
    reloadBirthdays,
    reloadOrders,
    reloadMentions,
    reloadCompletions,
    reloadBillingQuestions,
    reloadAngebote,
    reloadMgmtMetrics,
    reloadAtContact,
    reloadBillingInbox,
    reloadOfficeCounts,
    reloadReadyMaterial,
  ]);

  useEffect(() => {
    void reloadOrders();
    void reloadMentions();
    void reloadCompletions();
    void reloadBillingQuestions();
    void reloadAngebote();
    void reloadMgmtMetrics();
    void reloadAtContact();
    void reloadBillingInbox();
    void reloadOfficeCounts();
    void reloadReadyMaterial();
  }, [
    reloadOrders,
    reloadMentions,
    reloadCompletions,
    reloadBillingQuestions,
    reloadAngebote,
    reloadMgmtMetrics,
    reloadAtContact,
    reloadBillingInbox,
    reloadOfficeCounts,
    reloadReadyMaterial,
  ]);

  useEffect(() => {
    if (persona === "management") {
      void reloadCompletions();
      void reloadAtContact();
    }
    if (isAbrechnung) {
      void reloadBillingInbox();
    }
  }, [timesheetRefreshKey, persona, isAbrechnung, reloadCompletions, reloadAtContact, reloadBillingInbox]);

  useRealtime({
    enabled: !!user,
    scopes: [
      "domain:auftraege",
      "domain:projects",
      "domain:timesheets",
      "auftraege:list",
      "projects:list",
      "timesheets:list",
      "badge:project-completions",
      "domain:angebotsanfragen",
      "angebotsanfragen:list",
    ],
    domains: [
      "auftraege",
      "projects",
      "timesheets",
      "angebotsanfragen",
      ...(persona === "management" || persona === "billing" ? ["dokumente"] : []),
    ],
    events: [
      "angebotsanfragen_changed",
      "auftrag_neu",
      "auftraege_changed",
      "projects_changed",
      "timesheets_changed",
      "timesheet_created",
      "timesheet_updated",
      "timesheet_status_changed",
      "timesheet_company_signed",
      "timesheet_customer_signed",
      "timesheet_deleted",
      "timesheet_pdf_generated",
      "timesheet_billing_question_created",
      "timesheet_billing_question_answered",
      "timesheet_billing_seen",
      "timesheet_billing_assigned",
      "timesheet_billing_unassigned",
      ...(persona === "management" || persona === "billing" ? ["dokumente_changed"] : []),
    ],
    onChange: reloadAll,
  });

  useRealtime({
    enabled: !!user,
    scopes: ["domain:mentions", "mentions:inbox", "badge:mentions"],
    domains: ["mentions"],
    events: [...MENTIONS_BADGE_EVENTS],
    onChange: reloadMentions,
  });

  useRealtime({
    enabled: !!user && isMonteur,
    scopes: [
      "domain:material_anforderungen",
      "domain:material-anforderungen",
      "material_anforderungen:list",
      "badge:material",
    ],
    domains: ["material_anforderungen", "material-anforderungen"],
    events: [
      "material_anforderungen_changed",
      "material-anforderungen_changed",
      "material_anforderung_changed",
      "orders_changed",
    ],
    onChange: reloadReadyMaterial,
  });

  useEffect(() => {
    const onMentionsChanged = () => {
      void reloadMentions();
    };
    window.addEventListener("mentions_changed", onMentionsChanged);
    return () => window.removeEventListener("mentions_changed", onMentionsChanged);
  }, [reloadMentions]);

  const openCompletions = completions ?? [];
  const openAngebote = angebote ?? [];
  const openBillingQuestions = billingQuestions ?? [];
  const openDocBillingQuestions = docBillingQuestions ?? [];
  const openBillingQuestionTotal =
    openBillingQuestions.reduce(
      (sum, row) => sum + getOpenBillingQuestionCount(row),
      0,
    ) +
    openDocBillingQuestions.reduce(
      (sum, row) => sum + Math.max(0, Number(row.billing_question_open_count) || 0),
      0,
    );

  const atContactRows = atContactNotes ?? [];
  const signedTimesheets = billingInbox?.signed ?? [];
  const draftTimesheets = billingInbox?.drafts ?? [];
  const questionTimesheets = billingInbox?.questions ?? [];
  const newLieferscheine = billingInbox?.lieferscheine ?? [];

  const kpiItems = useMemo((): HomeKpiItem[] => {
    if (persona === "management") {
      return [
        {
          key: "completions",
          label: "Fertigstellungen offen",
          value: completions === null ? "—" : String(openCompletions.length),
          href: "#home-completions",
          alert: openCompletions.length > 0,
        },
        {
          key: "at-contact",
          label: "Lieferscheine beim AP",
          value: atContactNotes === null ? "—" : String(atContactRows.length),
          href: "#home-lieferscheine",
          alert: atContactRows.length > 0,
        },
        {
          key: "angebote",
          label: "Angebotsanfragen",
          value: angebote === null ? "—" : String(openAngebote.length),
          href: "#home-angebote",
          alert: openAngebote.length > 0,
        },
        {
          key: "questions",
          label: "Rückfragen offen",
          value: billingQuestions === null ? "—" : String(openBillingQuestionTotal),
          href: "#home-rueckfragen",
          alert: openBillingQuestionTotal > 0,
        },
        {
          key: "hours",
          label: "Abgerechnete Stunden",
          value:
            mgmtMetrics && !mgmtMetricsError
              ? formatHoursMetric(mgmtMetrics.billedHours)
              : "—",
          href: "/auswertungen",
          title: "Summe aus Auswertung Stunden-Pipeline (Rechnung erstellt + bezahlt)",
        },
        {
          key: "invoice",
          label: "Rechnungswert",
          value:
            mgmtMetrics && !mgmtMetricsError
              ? formatCurrencyMetric(mgmtMetrics.invoiceValue)
              : "—",
          href: "/auswertungen",
          title: "Gesamtumsatz aus Auswertung Umsatz & Zahlungen",
        },
      ];
    }
    if (persona === "billing") {
      const pending = billingInbox === null && !billingInboxError;
      const shown = (count: number) => (pending ? "—" : String(count));
      return [
        {
          key: "signed",
          label: "Unterschrieben",
          value: shown(signedTimesheets.length),
          href: "#home-billing-signed",
          alert: signedTimesheets.length > 0,
        },
        {
          key: "lieferscheine",
          label: "Neue Lieferscheine",
          value: shown(newLieferscheine.length),
          href: "#home-billing-lieferscheine",
          alert: newLieferscheine.length > 0,
        },
        {
          key: "drafts",
          label: "Rechnungsentwürfe",
          value: shown(draftTimesheets.length),
          href: "#home-billing-drafts",
          alert: draftTimesheets.length > 0,
        },
        {
          key: "rueckfragen",
          label: "Mit Rückfrage",
          value: shown(questionTimesheets.length),
          href: "#home-billing-questions",
          alert: questionTimesheets.length > 0,
        },
        {
          key: "missing-numbers",
          label: "Ohne Nummer",
          value:
            badgeCounts["kontakte-ohne-nummer"] == null
              ? "—"
              : String(badgeCounts["kontakte-ohne-nummer"]),
          href: "#home-billing-missing-numbers",
          alert: (badgeCounts["kontakte-ohne-nummer"] ?? 0) > 0,
        },
      ];
    }
    if (persona === "office") {
      const pending = officeCounts === null && !officeCountsError;
      const shown = (count: number) => (pending ? "—" : String(count));
      const materialOpen = Math.max(
        0,
        (officeCounts?.materialOpen ?? 0) - (officeCounts?.materialUrgent ?? 0),
      );
      return [
        {
          key: "urgent",
          label: "Fixtermine",
          value: shown(officeCounts?.materialUrgent ?? 0),
          href: "/bestellwesen/materialanforderungen",
          alert: (officeCounts?.materialUrgent ?? 0) > 0,
        },
        {
          key: "material",
          label: "Material offen",
          value: shown(materialOpen),
          href: "/bestellwesen/materialanforderungen",
          alert: materialOpen > 0,
        },
        {
          key: "feinplanung",
          label: "Feinplanung",
          value: shown(officeCounts?.feinplanung ?? 0),
          href: "/montageplanung",
          alert: (officeCounts?.feinplanung ?? 0) > 0,
        },
        {
          key: "angebote",
          label: "Angebote offen",
          value: shown(officeCounts?.angebote ?? 0),
          href: "/angebotsanfragen",
          alert: (officeCounts?.angebote ?? 0) > 0,
        },
        {
          key: "mahnungen",
          label: "Mahnungen",
          value: shown(officeCounts?.mahnungen ?? 0),
          href: "/dokumente/mahnwesen",
          alert: (officeCounts?.mahnungen ?? 0) > 0,
        },
        {
          key: "mentions",
          label: "Erwähnungen",
          value: String(mentions.length),
          href: "#home-mentions",
          alert: mentions.length > 0,
        },
      ];
    }
    if (persona === "field") {
      return [
        {
          key: "material",
          label: "Material bereit",
          value: readyMaterial === null ? "—" : String(readyMaterial.length),
          href: "/materialanforderungen",
          alert: (readyMaterial?.length ?? 0) > 0,
        },
        {
          key: "mentions",
          label: "Erwähnungen",
          value: String(mentions.length),
          href: "#home-mentions",
          alert: mentions.length > 0,
        },
      ];
    }
    return [
      {
        key: "mentions",
        label: "Erwähnungen",
        value: String(mentions.length),
        href: "#home-mentions",
        alert: mentions.length > 0,
      },
      {
        key: "birthdays",
        label: "Geburtstage heute",
        value: String(birthdays?.todays.length ?? 0),
      },
    ];
  }, [
    persona,
    completions,
    openCompletions.length,
    atContactNotes,
    atContactRows.length,
    angebote,
    openAngebote.length,
    billingQuestions,
    openBillingQuestionTotal,
    mgmtMetrics,
    mgmtMetricsError,
    billingInbox,
    billingInboxError,
    signedTimesheets.length,
    newLieferscheine.length,
    draftTimesheets.length,
    questionTimesheets.length,
    badgeCounts,
    officeCounts,
    officeCountsError,
    mentions.length,
    readyMaterial,
    birthdays?.todays.length,
  ]);


  const [homeLayout, setHomeLayout] = useState<HomeLayoutSettings | null>(null);
  const [arrangingHome, setArrangingHome] = useState(false);
  const [homeLayoutError, setHomeLayoutError] = useState<string | null>(null);
  const homeLayoutDirty = useRef(false);
  const homeLayoutSeq = useRef(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void getHomeLayout()
      .then((layout) => {
        if (!cancelled && !homeLayoutDirty.current) setHomeLayout(layout);
      })
      .catch(() => {
        if (!cancelled && !homeLayoutDirty.current) setHomeLayout(DEFAULT_HOME_LAYOUT);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const persistHomeLayout = useCallback((layout: HomeLayoutSettings) => {
    homeLayoutDirty.current = true;
    setHomeLayout(layout);
    const seq = ++homeLayoutSeq.current;
    setHomeLayoutError(null);
    void saveHomeLayout(layout).catch(() => {
      if (homeLayoutSeq.current === seq) {
        setHomeLayoutError("Die Startseite konnte nicht gespeichert werden.");
      }
    });
  }, []);

  const showMaterialTile =
    isMonteur &&
    (readyMaterial === null || readyMaterial.length > 0 || Boolean(readyMaterialError));
  const ordersFullWidth = persona !== "office";
  const layout = homeLayout ?? DEFAULT_HOME_LAYOUT;
  const tileAudience = useMemo(() => homeTileAudience(user), [user]);
  const permittedHomeTiles = useMemo(
    () => permittedHomeTileIds(tileAudience, Boolean(user)),
    [tileAudience, user],
  );
  const defaultTiles = useMemo(
    () =>
      defaultHomeTileIds(tileAudience, {
        hasUser: Boolean(user),
        hasKpis: kpiItems.length > 0,
        showMaterial: showMaterialTile,
      }),
    [tileAudience, user, kpiItems.length, showMaterialTile],
  );
  const orderedHomeTiles = useMemo(
    () => visibleHomeTileIds(permittedHomeTiles, defaultTiles, layout),
    [permittedHomeTiles, defaultTiles, layout],
  );
  const visibleHomeTileSet = useMemo(() => new Set(orderedHomeTiles), [orderedHomeTiles]);

  const moveHomeTile = (id: HomeTileId, delta: -1 | 1) => {
    const visible = visibleHomeTileIds(permittedHomeTiles, defaultTiles, layout);
    const index = visible.indexOf(id);
    const nextIndex = index + delta;
    if (index < 0 || nextIndex < 0 || nextIndex >= visible.length) return;
    const nextVisible = visible.slice();
    const item = nextVisible[index];
    if (item == null) return;
    nextVisible.splice(index, 1);
    nextVisible.splice(nextIndex, 0, item);
    persistHomeLayout({
      ...layout,
      order: weaveHomeOrder(layout.order, nextVisible),
    });
  };

  const dropHomeTile = (fromId: HomeTileId, toId: HomeTileId) => {
    if (fromId === toId) return;
    const visible = visibleHomeTileIds(permittedHomeTiles, defaultTiles, layout);
    const fromIndex = visible.indexOf(fromId);
    const toIndex = visible.indexOf(toId);
    if (fromIndex < 0 || toIndex < 0) return;
    const nextVisible = visible.slice();
    const item = nextVisible[fromIndex];
    if (item == null) return;
    nextVisible.splice(fromIndex, 1);
    nextVisible.splice(toIndex, 0, item);
    persistHomeLayout({
      ...layout,
      order: weaveHomeOrder(layout.order, nextVisible),
    });
  };

  const resetHomeLayout = () => persistHomeLayout(DEFAULT_HOME_LAYOUT);

  const toggleHomeTile = (id: HomeTileId) => {
    if (!permittedHomeTiles.includes(id)) return;
    const visible = visibleHomeTileIds(permittedHomeTiles, defaultTiles, layout);
    persistHomeLayout(
      visible.includes(id)
        ? hideHomeTile(layout, id, permittedHomeTiles, defaultTiles)
        : showHomeTile(layout, id, permittedHomeTiles, defaultTiles),
    );
  };

  const changeHomeColumns = (columns: HomeColumnCount) => {
    persistHomeLayout(setHomeColumns(layout, columns));
  };

  const changeHomeTileSpan = (id: HomeTileId, span: HomeTileSpan) => {
    const clamped: HomeTileSpan = span > layout.columns ? layout.columns : span;
    persistHomeLayout(setHomeTileSpan(layout, id, clamped));
  };

  const billingCard = (kind: "signed" | "lieferscheine" | "drafts" | "questions") => (
    <BillingHomeCard
      kind={kind}
      signed={signedTimesheets}
      drafts={draftTimesheets}
      questions={questionTimesheets}
      lieferscheine={newLieferscheine}
      loading={billingInbox === null && !billingInboxError}
      error={billingInboxError}
      onOpenTimesheet={(timesheetId) => openDetail(timesheetId)}
      onOpenLieferschein={(dokumentId, auftragId) => openLieferscheinDetail(dokumentId, auftragId)}
    />
  );

  const homeTileNode = (id: HomeTileId): ReactNode => {
    switch (id) {
      case "kpis":
        return <HomeKpiStrip items={kpiItems} />;
      case "todos":
        return <TodoPanel />;
      case "absences":
        return <HomeApprovalsCards enabled={canApproveZeiterfassung} />;
      case "my-day":
        return <MyDayPanel />;
      case "material":
        return (
          <FieldMaterialCard
            items={readyMaterial}
            loading={readyMaterial === null && !readyMaterialError}
            error={readyMaterialError}
            markingId={markingMaterialId}
            onMarkSeen={(materialId) => void markReadyMaterialSeen(materialId)}
          />
        );
      case "completions":
        return (
          <Card
              id="home-completions"
              className="eh-home-anchor"
              title={
                <span className="inline-flex flex-wrap items-center gap-2">
                  Offene Fertigstellungsmeldungen
                  {openCompletions.length > 0 ? (
                    <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red">
                      {openCompletions.length} offen
                    </span>
                  ) : null}
                </span>
              }
              actions={
                <Link
                  to="/dokumente/fertigstellungsmeldungen"
                  className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
                >
                  Zur Übersicht
                </Link>
              }
              bodyClassName="!p-0"
            >
              <ProjectCompletionsTable
                rows={openCompletions}
                loading={completions === null && !completionsError}
                error={completionsError}
                onWriteLieferschein={(row) => {
                  if (row.id == null) return;
                  openLieferscheinForProject({
                    id: row.id,
                    auftragsnummer: row.auftragsnummer,
                    kunde: row.kunde,
                    ort: row.ort,
                    hero_project_id:
                      row.hero_project_id != null ? Number(row.hero_project_id) : null,
                    auftragsart: row.auftragsart,
                  });
                }}
              />
            </Card>
        );
      case "lieferscheine":
        return (
          <LieferscheineAtContactCard
              rows={atContactRows}
              loading={atContactNotes === null && !atContactError}
              error={atContactError}
              onOpen={(dokumentId) => openDocumentEditor({ docId: dokumentId })}
            />
        );
      case "angebote":
        return (
          <Card
              id="home-angebote"
              className="eh-home-anchor"
              title={
                <span className="inline-flex flex-wrap items-center gap-2">
                  Offene Angebotsanfragen
                  {openAngebote.length > 0 ? (
                    <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red">
                      {openAngebote.length} offen
                    </span>
                  ) : null}
                </span>
              }
              actions={
                <Link
                  to="/angebotsanfragen"
                  className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
                >
                  Zur Übersicht
                </Link>
              }
              bodyClassName="!p-0"
            >
              <AngebotsanfragenTable
                rows={openAngebote}
                loading={angebote === null && !angeboteError}
                error={angeboteError}
              />
            </Card>
        );
      case "rueckfragen":
        return (
          <Card
              id="home-rueckfragen"
              className="eh-home-anchor"
              title={
                <span className="inline-flex flex-wrap items-center gap-2">
                  Offene Abrechnungs-Rückfragen
                  {openBillingQuestionTotal > 0 ? (
                    <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red">
                      {openBillingQuestionTotal} offen
                    </span>
                  ) : null}
                </span>
              }
              actions={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setAnsweredBillingOpen(true)}
                    className="inline-flex items-center rounded-full border border-separator px-3 py-1 text-xs font-medium text-label hover:bg-fill-tertiary"
                  >
                    Beantwortete
                  </button>
                  <Link
                    to="/stundennachweise"
                    className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
                  >
                    Zu Stundennachweisen
                  </Link>
                </div>
              }
              bodyClassName="!p-0"
            >
              <OpenBillingQuestionsTable
                rows={openBillingQuestions}
                docRows={openDocBillingQuestions}
                loading={billingQuestions === null && !billingQuestionsError}
                error={billingQuestionsError}
                onOpen={(timesheetId) => openDetail(timesheetId, { initialTab: "billing" })}
                onOpenDokument={(dokumentId) => openDocumentEditor({ docId: dokumentId })}
                onOpenProjectOverview={openProjectOverview}
              />
              <AnsweredBillingTimesheetsDialog
                open={answeredBillingOpen}
                onClose={() => setAnsweredBillingOpen(false)}
                onOpenTimesheet={(timesheetId) =>
                  openDetail(timesheetId, { initialTab: "billing" })
                }
                onOpenProjectOverview={openProjectOverview}
              />
            </Card>
        );
      case "billing-signed":
        return billingCard("signed");
      case "billing-lieferscheine":
        return billingCard("lieferscheine");
      case "billing-drafts":
        return billingCard("drafts");
      case "billing-questions":
        return billingCard("questions");
      case "billing-missing-numbers":
        return <HomeMissingNumbersTile />;
      case "office-points":
        return (
          <OfficeOpenPoints
                  counts={officeCounts}
                  loading={officeCounts === null && !officeCountsError}
                  error={officeCountsError}
                />
        );
      case "orders":
        return (
          <Card title="Letzte Aufträge" description="letzte 5" bodyClassName="!p-0">
                <LastOrdersTable
                  rows={orders}
                  loading={orders === null && !ordersError}
                  error={ordersError}
                />
              </Card>
        );
      case "personal-overview":
        return <HomePersonalOverviewTile />;
      case "personal-absences":
        return <HomePersonalAbsencesTile />;
      case "team-absences":
        return <TeamAbsencesCard enabled />;
      case "subcontractors":
        return <SubcontractorOpenCard enabled />;
      case "bw-material-open":
      case "bw-material-work":
      case "bw-inquiries":
      case "bw-orders-late":
        return <HomeBestellwesenTile tile={id} />;
      case "mentions":
        return (
          <Card
            id="home-mentions"
            className="eh-home-anchor"
            title={
              <span className="inline-flex items-center gap-2">
                Erwähnungen
                {mentions.length > 0 ? (
                  <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
                    {mentions.length} offen
                  </span>
                ) : null}
              </span>
            }
            actions={
              <Button
                variant="secondary"
                size="sm"
                type="button"
                disabled={readMentions.length === 0}
                onClick={() => setMentionsHistoryOpen(true)}
              >
                Verlauf
              </Button>
            }
            bodyClassName="!p-4"
          >
            {mentionsError ? (
              <Alert variant="error">{mentionsError}</Alert>
            ) : mentions.length === 0 ? (
              <p className="text-sm text-label-secondary">
                <em>Keine offenen Erwähnungen.</em>
              </p>
            ) : (
              <div className="eh-home-feed-list">
                {(showAllMentions ? mentions : mentions.slice(0, MENTIONS_PREVIEW_LIMIT)).map(
                  (m) => (
                    <article
                      key={m.id}
                      className="eh-home-feed-item eh-home-feed-item--clickable"
                      onClick={() => replyToMention(m)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") replyToMention(m);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-md bg-fill px-2 py-0.5 text-label-secondary">
                          {formatDateLabel(m.note_created_at)}
                        </span>
                        <span className="text-label-secondary">
                          {m.auftragsnummer || "Projekt"}
                        </span>
                      </div>
                      <div className="mb-2 text-sm text-label-secondary">
                        <MentionBody
                          mention={m}
                          compact
                          onRefClick={(type, id, label) => {
                            void handleRefClick(type, id, label);
                          }}
                        />
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-label-tertiary">
                          von {m.note_author || "Unbekannt"}
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {showTodoPanel ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMentionTodoDraft(m);
                              }}
                            >
                              Als To-Do
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              replyToMention(m);
                            }}
                          >
                            Antworten
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openMentionInProjectOverview(m);
                            }}
                          >
                            Ansehen
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void markMentionRead(m.id).then(() => {
                                completeMentionRead(m);
                              });
                            }}
                          >
                            Als gelesen
                          </Button>
                        </div>
                      </div>
                    </article>
                  ),
                )}
                {mentions.length > MENTIONS_PREVIEW_LIMIT ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full border-dashed"
                    type="button"
                    onClick={() => setShowAllMentions((v) => !v)}
                  >
                    {showAllMentions
                      ? "Kompakt anzeigen"
                      : `${mentions.length - MENTIONS_PREVIEW_LIMIT} weitere anzeigen`}
                  </Button>
                ) : null}
              </div>
            )}
          </Card>
        );
      default:
        return null;
    }
  };

  const homeTiles: HomeBoardTile[] = orderedHomeTiles.map((id) => ({
    id,
    span: resolveHomeTileSpan(id, layout.columns, layout.spans, ordersFullWidth),
    node: homeTileNode(id),
  }));

  const wantsPersonalSelf =
    visibleHomeTileSet.has("personal-overview") || visibleHomeTileSet.has("personal-absences");
  const wantsBestellwesen =
    visibleHomeTileSet.has("bw-material-open") ||
    visibleHomeTileSet.has("bw-material-work") ||
    visibleHomeTileSet.has("bw-inquiries") ||
    visibleHomeTileSet.has("bw-orders-late");

  return (
    <AppShell
      title={HOME_PERSONA_TITLE[persona]}
      subtitle={HOME_PERSONA_SUBTITLE[persona]}
      className={clsx(showHomeBirthdayEffects && "eh-home--birthday-self")}
      actions={
        arrangingHome ? (
          <>
            <Button variant="secondary" size="sm" type="button" onClick={resetHomeLayout}>
              Standard
            </Button>
            <Button size="sm" type="button" onClick={() => setArrangingHome(false)}>
              Fertig
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" type="button" onClick={() => setArrangingHome(true)}>
            Anordnen
          </Button>
        )
      }
    >
      {showHomeBirthdayWidget ? (
        <BirthdayWidget
          me={birthdays!.me}
          todays={birthdays!.todays}
          subtle={decoPreference === "subtle"}
        />
      ) : null}

      <HomePersonalDataProvider login={wantsPersonalSelf ? getUserLocalLogin(user) : ""}>
        <HomeBestellwesenProvider enabled={wantsBestellwesen}>
          <HomeTileBoard
            tiles={homeTiles}
            arranging={arrangingHome}
            columns={layout.columns}
            permittedIds={permittedHomeTiles}
            visibleIds={visibleHomeTileSet}
            saveError={homeLayoutError}
            onMove={moveHomeTile}
            onDropOn={dropHomeTile}
            onColumnsChange={changeHomeColumns}
            onSpanChange={changeHomeTileSpan}
            onToggleTile={toggleHomeTile}
            onRemove={toggleHomeTile}
          />
        </HomeBestellwesenProvider>
      </HomePersonalDataProvider>

      <MentionsHistoryDialog
        open={mentionsHistoryOpen}
        items={readMentions}
        showTodoPanel={showTodoPanel}
        onClose={() => setMentionsHistoryOpen(false)}
        onOpen={(mention) => {
          setMentionsHistoryOpen(false);
          openMentionInProjectOverview(mention);
        }}
        onReply={(mention) => {
          setMentionsHistoryOpen(false);
          replyToMention(mention);
        }}
        onCreateTodo={(mention) => {
          setMentionsHistoryOpen(false);
          setMentionTodoDraft(mention);
        }}
        onRefClick={(type, id, label) => {
          void handleRefClick(type, id, label);
        }}
      />

      {mentionTodoDraft ? (
        <TodoQuickDialog
          open
          onClose={() => setMentionTodoDraft(null)}
          title="Erwähnung als To-Do übernehmen"
          description={
            mentionTodoDraft.auftragsnummer
              ? `Aus Projekt ${mentionTodoDraft.auftragsnummer} – Text anpassen und optional eine Wiedervorlage setzen.`
              : "Text anpassen und optional eine Wiedervorlage setzen."
          }
          draft={buildMentionTodoDraft(mentionTodoDraft)}
          successMessage="To-Do erstellt – Erwähnung als gelesen markiert."
          onCreated={() => {
            const mention = mentionTodoDraft;
            void markMentionRead(mention.id).then(() => {
              completeMentionRead(mention);
            });
          }}
        />
      ) : null}

      {fallbackModal}
    </AppShell>
  );
}

function LastOrdersTable({
  rows,
  loading,
  error,
}: {
  rows: HomeOrderRow[] | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return <p className="px-4 py-6 text-sm text-status-red">{error}</p>;
  }
  if (!rows?.length) {
    return (
      <p className="px-4 py-6 text-sm text-label-secondary">Keine Aufträge gefunden.</p>
    );
  }

  return (
    <ul className="eh-home-queue eh-home-queue-scroll" role="list">
      {rows.map((o) => {
        const heroId = o.hero_project_id;
        const meta = [o.ordnernummer, o.ort_name || o.ort].filter(Boolean).join(" · ");
        return (
          <li key={String(o.id ?? o.auftragsnummer)} className="eh-home-queue-card">
            <div className="eh-home-queue-card__row">
              <span className="eh-home-queue-row__kicker">{o.auftragsnummer || "—"}</span>
              <span className="eh-home-queue-row__copy">
                <span className="eh-home-queue-row__title">{o.beschreibung || "—"}</span>
                {meta ? <span className="eh-home-queue-row__meta">{meta}</span> : null}
              </span>
              {heroId ? (
                <a
                  href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(heroId))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0"
                  aria-label="Hero-Projekt öffnen"
                >
                  <HeroHeaderLogo className="h-[18px] w-auto" />
                </a>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function OpenBillingQuestionsTable({
  rows,
  docRows,
  loading,
  error,
  onOpen,
  onOpenDokument,
  onOpenProjectOverview,
}: {
  rows: TimesheetSummary[];
  docRows: DokumentMitOffenenRueckfragen[];
  loading: boolean;
  error: string | null;
  onOpen: (timesheetId: number) => void;
  onOpenDokument: (dokumentId: number) => void;
  onOpenProjectOverview: (auftragId: number) => void;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (error && !docRows.length) {
    return <p className="px-4 py-6 text-sm text-status-red">{error}</p>;
  }
  if (!rows.length && !docRows.length) {
    return (
      <p className="px-4 py-6 text-sm text-label-secondary">
        Keine offenen Abrechnungs-Rückfragen.
      </p>
    );
  }

  const questionLabel = (count: number) =>
    `${count} ${count === 1 ? "Rückfrage" : "Rückfragen"}`;

  return (
    <ul className="eh-home-queue eh-home-queue-scroll" role="list">
      {error ? <li className="px-2 text-sm text-status-red">{error}</li> : null}
      {rows.map((row) => {
        const openCount = getOpenBillingQuestionCount(row);
        const displayNumber = getTimesheetDisplayNumber(row) || "—";
        const projectLabel = [row.auftragsnummer, row.auftrag_display]
          .filter(Boolean)
          .join(" – ");
        const status = getTimesheetStatus(row);
        const meta = [row.kunde, timesheetStatusLabel(status)].filter(Boolean).join(" · ");
        return (
          <li key={String(row.id ?? displayNumber)} className="eh-home-queue-card">
            <div className="eh-home-queue-card__row">
              <span className="eh-home-queue-row__kicker">{displayNumber}</span>
              <span className="eh-home-queue-row__copy">
                <span className="eh-home-queue-row__title">{projectLabel || "—"}</span>
                <span className="eh-home-queue-row__meta">{meta || "—"}</span>
              </span>
              <span className="eh-home-queue-row__status eh-home-queue-row__status--show !bg-status-red/15 !text-status-red">
                {questionLabel(openCount)}
              </span>
            </div>
            {row.id ? (
              <div className="eh-home-queue-card__actions">
                <ProjectOverviewButton
                  auftragId={row.auftrag_id}
                  onOpenOverride={onOpenProjectOverview}
                  className="h-8 w-8 shrink-0"
                />
                <Button variant="secondary" size="sm" type="button" onClick={() => onOpen(row.id)}>
                  Öffnen
                </Button>
              </div>
            ) : null}
          </li>
        );
      })}
      {docRows.map((row) => {
        const openCount = Math.max(0, Number(row.billing_question_open_count) || 0);
        const meta = [row.kunde, dokumentTypLabel(row.dokument_typ)].filter(Boolean).join(" · ");
        return (
          <li key={`doc-${row.dokument_id}`} className="eh-home-queue-card">
            <div className="eh-home-queue-card__row">
              <span className="eh-home-queue-row__kicker">{row.display_number || "—"}</span>
              <span className="eh-home-queue-row__copy">
                <span className="eh-home-queue-row__title">
                  {row.auftrag_display || row.betreff || "—"}
                </span>
                <span className="eh-home-queue-row__meta">{meta || "—"}</span>
              </span>
              <span className="eh-home-queue-row__status eh-home-queue-row__status--show !bg-status-red/15 !text-status-red">
                {questionLabel(openCount)}
              </span>
            </div>
            <div className="eh-home-queue-card__actions">
              <ProjectOverviewButton
                auftragId={row.auftrag_id ?? null}
                onOpenOverride={onOpenProjectOverview}
                className="h-8 w-8 shrink-0"
              />
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => onOpenDokument(row.dokument_id)}
              >
                Öffnen
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ProjectCompletionsTable({
  rows,
  loading,
  error,
  onWriteLieferschein,
}: {
  rows: ProjectCompletionRow[];
  loading: boolean;
  error: string | null;
  onWriteLieferschein: (row: ProjectCompletionRow) => void;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return <p className="px-4 py-6 text-sm text-status-red">{error}</p>;
  }
  if (!rows.length) {
    return (
      <p className="px-4 py-6 text-sm text-label-secondary">
        Keine offenen Fertigstellungsmeldungen.
      </p>
    );
  }

  return (
    <ul className="eh-home-queue eh-home-queue-scroll" role="list">
      {rows.map((row) => {
        const status = getProjectCompletionStatusMeta(row);
        const title = row.beschreibung?.trim() || "Fertigstellung";
        const meta = [row.kunde, row.ansprechpartner?.trim()].filter(Boolean).join(" · ");
        const heroId = row.hero_project_id;
        return (
          <li key={String(row.id ?? title)} className="eh-home-queue-card">
            <div className="eh-home-queue-card__row">
              <span className="eh-home-queue-row__kicker">
                {row.auftragsnummer || `#${row.id ?? "—"}`}
              </span>
              <span className="eh-home-queue-row__copy">
                <span className="eh-home-queue-row__title">{title}</span>
                <span className="eh-home-queue-row__meta">{meta || "—"}</span>
              </span>
              <span className="eh-home-queue-row__status eh-home-queue-row__status--show !bg-status-red/15 !text-status-red">
                {status.label || "Offen"}
              </span>
            </div>
            <div className="eh-home-queue-card__actions">
              {heroId ? (
                <a
                  href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(heroId))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-label-secondary hover:text-accent"
                >
                  <HeroHeaderLogo className="h-[14px] w-auto" />
                  Hero
                </a>
              ) : null}
              {row.id != null ? (
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => onWriteLieferschein(row)}
                >
                  Lieferschein schreiben
                </Button>
              ) : (
                <span className="text-xs text-label-tertiary">Kein Auftrag</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function AngebotsanfragenTable({
  rows,
  loading,
  error,
}: {
  rows: AgaListItem[];
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return <p className="px-4 py-6 text-sm text-status-red">{error}</p>;
  }
  if (!rows.length) {
    return (
      <p className="px-4 py-6 text-sm text-label-secondary">
        Keine offenen Angebotsanfragen.
      </p>
    );
  }

  return (
    <ul className="eh-home-queue eh-home-queue-scroll" role="list">
      {rows.map((row) => {
        const projectLabel =
          [row.auftrag_nummer, row.auftrag_beschreibung].filter(Boolean).join(" – ") || "—";
        const meta = [row.contact_display, row.created_by_name || row.created_by]
          .filter(Boolean)
          .join(" · ");
        return (
          <li key={row.id}>
            <Link to={`/angebotsanfragen?id=${row.id}`} className="eh-home-queue-row">
              <span className="eh-home-queue-row__kicker">{row.aga_nummer}</span>
              <span className="eh-home-queue-row__copy">
                <span className="eh-home-queue-row__title">{projectLabel}</span>
                <span className="eh-home-queue-row__meta">{meta || "—"}</span>
              </span>
              <ChevronRightIcon className="eh-home-queue-row__go" aria-hidden="true" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}


function MentionsHistoryDialog({
  open,
  items,
  showTodoPanel,
  onClose,
  onOpen,
  onReply,
  onCreateTodo,
  onRefClick,
}: {
  open: boolean;
  items: HomeMention[];
  showTodoPanel: boolean;
  onClose: () => void;
  onOpen: (mention: HomeMention) => void;
  onReply: (mention: HomeMention) => void;
  onCreateTodo: (mention: HomeMention) => void;
  onRefClick: (type: string, id: string, label: string) => void;
}) {
  return (
    <FeedDetailDialog open={open} title="Bereits gelesene Erwähnungen" onClose={onClose}>
      {!items.length ? (
        <p className="text-label-secondary">
          <em>Noch keine gelesenen Erwähnungen.</em>
        </p>
      ) : (
        <div className="eh-home-feed-list">
          {items.map((m) => (
            <article
              key={m.id}
              className="eh-home-feed-item eh-home-feed-item--clickable"
              onClick={() => onReply(m)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onReply(m);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-md bg-fill px-2 py-0.5 text-label-secondary">
                  {formatDateLabel(m.note_created_at)}
                </span>
                <span className="text-label-secondary">{m.auftragsnummer || "Projekt"}</span>
              </div>
              <div className="mb-2 text-sm text-label-secondary">
                <MentionBody
                  mention={m}
                  compact
                  onRefClick={onRefClick}
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-label-tertiary">
                  von {m.note_author || "Unbekannt"}
                </span>
                <div className="flex flex-wrap gap-2">
                  {showTodoPanel ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateTodo(m);
                      }}
                    >
                      Als To-Do
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReply(m);
                    }}
                  >
                    Antworten
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(m);
                    }}
                  >
                    Ansehen
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </FeedDetailDialog>
  );
}
